import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import axios from 'axios';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('認証トークンが見つかりません');
        return;
      }

      try {
        const response = await axios.get(`/api/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.message || 'メールアドレスが確認されました');
      } catch (error: any) {
        setStatus('error');
        const errorMessage = error.response?.data?.error?.message || 'メール認証に失敗しました';
        setMessage(errorMessage);
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleLoginClick = () => {
    navigate('/auth/login');
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%',
          }}
        >
          {status === 'loading' && (
            <>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                メールアドレスを確認しています...
              </Typography>
              <Typography color="text.secondary">
                しばらくお待ちください
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckIcon
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  mb: 2,
                }}
              />
              <Typography variant="h4" gutterBottom color="success.main">
                認証成功！
              </Typography>
              <Typography variant="body1" sx={{ mb: 4 }}>
                {message}
              </Typography>
              <Alert severity="success" sx={{ mb: 3 }}>
                メールアドレスの確認が完了しました。ログインしてサービスをご利用ください。
              </Alert>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleLoginClick}
                sx={{ mb: 2 }}
              >
                ログインページへ
              </Button>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={handleHomeClick}
              >
                ホームへ戻る
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorIcon
                sx={{
                  fontSize: 80,
                  color: 'error.main',
                  mb: 2,
                }}
              />
              <Typography variant="h4" gutterBottom color="error.main">
                認証エラー
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                {message}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                以下の原因が考えられます：
              </Typography>
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Typography variant="body2" component="li">
                  リンクの有効期限が切れている（24時間）
                </Typography>
                <Typography variant="body2" component="li">
                  既に認証済みのアカウント
                </Typography>
                <Typography variant="body2" component="li">
                  無効な認証リンク
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleLoginClick}
                sx={{ mb: 2 }}
              >
                ログインページへ
              </Button>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={handleHomeClick}
              >
                ホームへ戻る
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmailPage; 